
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import animateScrollTo from 'animated-scroll-to';
import { useRoute } from 'vue-router';
import getWork from '@/sanity/requests/work';
import WorkFormInputs from '@/components/WorkFormInputs.vue';
import WorksGrid from '@/components/WorksGrid.vue';

export default defineComponent({
  name: 'Work',
  components: {
    WorkFormInputs,
    WorksGrid,
  },
  setup() {
    const route = useRoute();
    const zoomed = ref(false);
    const resetState = () => {
      zoomed.value = false;
    };
    watch(() => route.params, resetState);

    let { data, loading } = getWork(route);

    const specs = computed(() => {
      if (!data.value) return [];
      const dimensions = Object.values(data.value.dimensions || {})
        .map((i) => `${i}`)
        .join(' × ');
      return [data.value.medium, dimensions ? `${dimensions} cm` : ''].filter(
        (i) => i
      );
    });

    const works = computed(() => data.value?.works || []);

    const onZoom = async (e: Event) => {
      zoomed.value = !zoomed.value;
      if (zoomed.value === true) {
        await nextTick();
        const target = e.target as HTMLElement;
        const pos = target.getBoundingClientRect().top + window.scrollY;
        window.scrollTo(0, pos);
      } else {
        window.scrollTo(0, 0);
      }
    };

    const multipleImages = computed(() => (data.value?.images.length || 0) > 1);

    const scrollTo = (selector: string, offset = 0) => {
      const [el] = document.querySelectorAll(selector);
      if (el !== null && el instanceof HTMLElement) {
        animateScrollTo(el.offsetTop - offset, {
          cancelOnUserAction: false,
          maxDuration: 1000,
        });
      }
    };

    // Enforce minimum height of image section
    // Based on text section height (absolutely positioned)
    const textElement = ref<HTMLElement | null>(null);
    const minHeight = ref(0);

    const setMinHeight = async () => {
      await nextTick();
      const textHeight = textElement.value?.offsetHeight || 0;
      minHeight.value = textHeight;
    };

    // set when loaded, and on resize
    watch(() => loading.value, setMinHeight);
    onMounted(() => window.addEventListener('resize', setMinHeight));
    onBeforeUnmount(() => window.removeEventListener('resize', setMinHeight));

    return {
      data,
      loading,
      minHeight,
      multipleImages,
      onZoom,
      textElement,
      scrollTo,
      specs,
      works,
      zoomed,
    };
  },
});
